import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { playDepositNotify, playMentionNotify, playNotify, playPortalNotify, playTaskNotify } from "../lib/notifySound";
import { useAuth } from "../context/AuthContext";

export function ChatNotifier() {
  const { user: me } = useAuth();
  const sinceRef = useRef(null);
  const taskSinceRef = useRef(null);
  const repeatRef = useRef([]);
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);

  useEffect(() => {
    if (!me?.id) return undefined;
    sinceRef.current = new Date().toISOString();
    taskSinceRef.current = sinceRef.current;
    let alive = true;
    // Ulangi bunyi deposit 3x (jeda 3 menit) selama masih ada request pending
    const scheduleRepeats = () => {
      repeatRef.current.forEach(clearTimeout);
      repeatRef.current = [1, 2].map((n) => setTimeout(async () => {
        if (!alive) return;
        try {
          const { data } = await api.get("/balance/topup-requests?status=pending");
          if (!data.length) return;
          playDepositNotify();
          toast.warning(`PENGINGAT: ${data.length} request saldo belum diproses`, { description: "Cek transfer masuk & setujui/tolak request deposit.", duration: 20000 });
        } catch { /* abaikan */ }
      }, n * 3 * 60 * 1000));
    };
    const check = async () => {
      if (isAdmin) {
        try {
          const { data: ev } = await api.get(`/admin-tasks/events?since=${encodeURIComponent(taskSinceRef.current)}`);
          if (alive && ev.length) {
            taskSinceRef.current = ev[0].at;
            const urgent = ev.some((e) => e.action === "dibuat" && e.priority === "urgent");
            if (urgent) playMentionNotify(); else playTaskNotify();
            const openTasks = { label: "Buka", onClick: () => { window.location.href = "/tugas-admin"; } };
            ev.slice(0, 3).forEach((e) => {
              const label = { dibuat: "TUGAS BARU", diterima: "Tugas diterima", progres: "Progres tugas", selesai: "LAPORAN SELESAI", ditolak: "Tugas ditolak", "dibuka-lagi": "Tugas dibuka lagi", diperiksa: "Tugas diperiksa" }[e.action] || e.action;
              const desc = `${e.by_name}${e.note ? ` — ${e.note.slice(0, 100)}` : ""}`;
              if (e.action === "dibuat" || e.action === "dibuka-lagi") toast.warning(`${label}: ${e.title}`, { description: desc, duration: 30000, action: openTasks });
              else if (e.action === "selesai") toast.success(`${label}: ${e.title}`, { description: desc, duration: 30000, action: openTasks });
              else toast.info(`${label}: ${e.title}`, { description: desc, duration: 12000, action: openTasks });
            });
          }
        } catch { /* abaikan */ }
      }
      try {
        const { data } = await api.get(`/chat/notifications?since=${encodeURIComponent(sinceRef.current)}`);
        if (!alive || !data.length) return;
        sinceRef.current = data[0].created_at;
        const hasDeposit = data.some((m) => m.channel === "system:topup");
        const hasPortal = data.some((m) => m.channel === "system:portal");
        const mention = data.find((m) => m.mentioned_me);
        if (mention) playMentionNotify(); else if (hasPortal) playPortalNotify(); else if (hasDeposit) playDepositNotify(); else playNotify();
        if (mention) {
          const goTasks = mention.channel === "admins:group";
          toast.error(`ANDA DI-TAG oleh ${mention.name}`, { description: mention.text, duration: 30000, action: { label: "Buka", onClick: () => (goTasks ? (window.location.href = "/tugas-admin") : window.dispatchEvent(new CustomEvent("fasnet:open-chat", { detail: mention.channel }))) } });
          if (!goTasks) window.dispatchEvent(new CustomEvent("fasnet:open-chat", { detail: mention.channel }));
        }
        const newRequest = data.some((m) => m.channel === "system:topup" && !(m.text || "").startsWith("Top-up saldo Anda") && !(m.text || "").startsWith("Request saldo Anda"));
        if (newRequest && isAdmin) scheduleRepeats();
        data.slice(0, 3).filter((m) => !m.mentioned_me).forEach((m) => {
          const text = m.text ? (m.text.length > 80 ? `${m.text.slice(0, 80)}…` : m.text) : "[Gambar]";
          const openChat = { label: "Balas", onClick: () => window.dispatchEvent(new CustomEvent("fasnet:open-chat", { detail: m.channel })) };
          if (m.channel === "admins:group") {
            toast.info(`GROUP ADMIN — ${m.name}`, { description: text, duration: 20000, action: { label: "Buka", onClick: () => { window.location.href = "/tugas-admin"; } } });
            return;
          }
          const isTopupOk = (m.text || "").startsWith("Top-up saldo Anda DISETUJUI");
          const isTopupNo = (m.text || "").startsWith("Request saldo Anda");
          if (isTopupOk) {
            toast.success(`TOP-UP DISETUJUI — ${m.name}`, { description: text, duration: 20000, action: openChat });
          } else if (isTopupNo) {
            toast.error(`REQUEST SALDO DITOLAK — ${m.name}`, { description: text, duration: 20000, action: openChat });
          } else if (m.channel === "system:topup") {
            toast.success(`REQUEST SALDO BARU — ${m.name}`, { description: text, duration: 20000 });
          } else if (m.channel === "system:portal") {
            const bukti = (m.text || "").includes("BUKTI TRANSFER");
            toast.warning(bukti ? "TAGIHAN PORTAL — BUKTI TRANSFER MASUK" : (m.text || "").includes("SUDAH TRANSFER") ? "TAGIHAN PORTAL — PELANGGAN SUDAH TRANSFER" : "TAGIHAN PORTAL BARU", { description: text, duration: 30000, action: { label: "Konfirmasi", onClick: () => window.dispatchEvent(new CustomEvent("fasnet:open-chat", { detail: "system:portal" })) } });
          } else if (m.channel === "system:hp-customer") {
            toast.warning("PENDAFTARAN PELANGGAN BANYAK HP", { description: text, duration: 25000, action: isAdmin ? { label: "Setujui", onClick: () => { window.location.href = "/pelanggan-hp"; } } : openChat });
          } else if ((m.text || "").startsWith("KOMPLAIN PPPoE")) {
            toast.error(`KOMPLAIN PPPoE BARU — ${m.name}`, { description: text, duration: 20000, action: isAdmin ? { label: "Lihat", onClick: () => { window.location.href = "/komplain-ppp"; } } : openChat });
            window.dispatchEvent(new CustomEvent("fasnet:complaints-changed"));
          } else if ((m.channel || "").startsWith("complaint:")) {
            toast.error(`Komplain baru masuk — ${m.name}`, { description: text, duration: 10000, action: openChat });
          } else if (m.pending) {
            toast.warning(`Chat AKTIVASI — ${m.name} menunggu ditautkan`, { description: text, duration: 15000, action: openChat });
          } else {
            toast.info(`Chat masuk — ${m.name}`, { description: text, action: openChat });
          }
        });
      } catch { /* retry di interval berikutnya */ }
    };
    const t = setInterval(check, 10000);
    return () => { alive = false; clearInterval(t); repeatRef.current.forEach(clearTimeout); };
  }, [me?.id]);

  return null;
}
